import { FC, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TransactionTypesOptions,
  withStyles,
  Card,
  Box,
  formatAmount,
  removeWhiteSpaceURI,
  TransactionStatusStepper,
} from 'vopay';
import { Title, UtilButton } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransactionHistory from '@/hooks/useApi/useTransactionHistory';
import styles from './TransactionCard.module.scss';

type TransactionCardProps = {
  receiverName: string;
  amount: string;
  currency: string;
  transactionId: string;
  parentTransactionId?: string;
  type: TransactionTypesOptions;
  parentType?: TransactionTypesOptions;
  failureReason: string;
  senderName?: string;
  contactId?: string;
  jwt?: string;
  isMainAccount: boolean;
  extraInformation?: ReactNode;
  additionalId?: ReactNode;
};

const cn = withStyles(styles);

const TransactionCard: FC<TransactionCardProps> = ({
  receiverName,
  amount,
  currency,
  transactionId,
  parentTransactionId,
  type,
  parentType = 'All',
  failureReason,
  senderName,
  contactId,
  jwt,
  isMainAccount,
  extraInformation,
  additionalId,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Card',
  });
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const toast = useToast();
  const { transactionHistory, fetchTransactionHistory } = useTransactionHistory({ bearer: jwt });

  const isWithdrawal = type.toLowerCase().includes('withdrawal');
  const isFunding = type.toLowerCase().includes('funding');

  useEffect(() => {
    if (!transactionId) return;

    (async () => {
      try {
        await fetchTransactionHistory({
          TransactionID: transactionId,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch transaction history' }));
      }
    })();
  }, [transactionId]);

  return (
    <Card className={cn('card')}>
      <Box jc="sb" gapSize="24px">
        <Box flx="col" gapSize="24px">
          <Box flx="col" gapSize="9px">
            <Title level="2">
              {type === 'Account Transfer' && senderName && !extraInformation ? (
                <>
                  {senderName} <span className={cn('grey')}>{t('to', { defaultValue: 'to' })}</span> {receiverName}
                </>
              ) : contactId && isMainAccount ? (
                <UtilButton
                  className={cn('name')}
                  onClick={() => {
                    if (clientAccountsEnabled) {
                      window.open(`/client-accounts/${contactId}`, '_blank');
                    } else {
                      window.open(`/contacts/${contactId}`, '_blank');
                    }
                  }}
                >
                  {receiverName}
                </UtilButton>
              ) : (
                receiverName
              )}
            </Title>
            {extraInformation}
          </Box>
          <Box flx="col" gapSize="32px" padding="0 0 0 6px">
            <Box flx="col" gapSize="5px">
              <Title level="5" emphasized={false} className={cn('grey')}>
                {isWithdrawal
                  ? t('amountSent', { defaultValue: 'Amount Sent' })
                  : isFunding
                    ? t('amountRequested', { defaultValue: 'Amount Requested' })
                    : t('amount', { defaultValue: 'Amount' })}
              </Title>
              <Title level="4">
                {currency} {formatAmount(amount)}
              </Title>
            </Box>

            <div className={cn('idContainer')}>
              <Box flx="col" gapSize="6px">
                <div className={cn('grey', 'row')}>{t('transactionId', { defaultValue: 'Transaction ID' })}</div>
                <div className={cn('fontWeight500')}>{transactionId}</div>
              </Box>
              {parentTransactionId && parentType && (
                <div className={cn('second')}>
                  <div className={cn('grey', 'row')}>
                    {t('parentTransactionId', { defaultValue: 'Parent Transaction ID' })}
                  </div>
                  <UtilButton
                    onClick={() => {
                      window.open(
                        `/transactions-history/details/${removeWhiteSpaceURI(parentType)}/${parentTransactionId}/${currency}`,
                        '_blank',
                      );
                    }}
                  >
                    {parentTransactionId}
                  </UtilButton>
                </div>
              )}
              {additionalId && <div className={cn('second')}>{additionalId}</div>}
            </div>
          </Box>
        </Box>

        <TransactionStatusStepper transactionHistory={transactionHistory} failureReason={failureReason} />
      </Box>
    </Card>
  );
};

export default TransactionCard;
